// @ts-check

import { mutationOptions, queryOptions } from "@tanstack/react-query"
import { generateChatReport, getChatSessionReports, updateChatReport } from "../api"
import { ChatSessionQueryKeys } from "../sessions"
import { handleStream, setStreamQueriesData } from "../streams"

const ChatReportQueryKeys = {
  /** @param {string} sessionId */
  CHAT_SESSION_REPORTS: (sessionId) => [
    ...ChatSessionQueryKeys.CHAT_SESSION(sessionId),
    "reports",
  ],
}

const ChatReportQueryOptions = {
  /**
   * @param {Object} params
   * @param {string} params.sessionId
   * @param {string} params.userToken
   */
  CHAT_SESSION_REPORTS: ({ sessionId, userToken }) => queryOptions({
    queryKey: ChatReportQueryKeys.CHAT_SESSION_REPORTS(sessionId),
    queryFn: ({ signal }) => (
      getChatSessionReports(sessionId, userToken, { signal })
        .then(data => data.reports)
    ),
    staleTime: Infinity,
    enabled: userToken != null && sessionId != null,
  }),
}

const ChatReportMutationOptions = {
  /**
   * @param {Object} params
   * @param {() => string} params.getUserToken
   * @param {(payload: import("../api").StreamEventsPayload) => void} [params.onStreamEvent]
   */
  GENERATE_REPORT: ({ getUserToken, onStreamEvent }) => mutationOptions({
    /**
     * @param {Object} params
     * @param {string} params.sessionId
     * @param {ChatReportInput} params.input
     */
    mutationFn: ({ sessionId, input }) => {
      const readable = generateChatReport(sessionId, input, getUserToken())
      return handleStream(readable, {
        sessionId,
        getUserToken,
        onStreamEvent,
      })
    },
    async onSuccess(data, variables, _, context) {
      await setStreamQueriesData(context.client, data, variables.sessionId)
    },
  }),
  /**
   * @param {Object} params
   * @param {string} params.sessionId
   * @param {string} params.userToken
   */
  UPDATE_REPORT: ({ sessionId, userToken }) => mutationOptions({
    /**
     * @param {Object} params
     * @param {string} params.reportId
     * @param {Partial<ChatReport>} params.reportData
     */
    mutationFn: ({ reportId, reportData }) => (
      updateChatReport(reportId, reportData, userToken)
    ),
    async onMutate(variables, context) {
      await context.client.cancelQueries({
        queryKey: ChatReportQueryKeys.CHAT_SESSION_REPORTS(sessionId)
      })

      const oldReport = context.client.getQueryData(
        ChatReportQueryKeys.CHAT_SESSION_REPORTS(sessionId)
      )
      context.client.setQueryData(
        ChatReportQueryKeys.CHAT_SESSION_REPORTS(sessionId),
        /** @param {ChatReport[]} reports */
        reports => reports?.map(report => {
          if (report._id !== variables.reportId) return report
          return { ...report, ...variables.reportData }
        })
      )
      return { report: oldReport }
    },
    async onSuccess(data, variables, _, context) {
      await context.client.cancelQueries({
        queryKey: ChatReportQueryKeys.CHAT_SESSION_REPORTS(sessionId)
      })

      context.client.setQueryData(
        ChatReportQueryKeys.CHAT_SESSION_REPORTS(sessionId),
        /** @param {ChatReport[]} reports */
        reports => reports?.map(report => {
          if (report._id !== variables.reportId) return report
          return data.report
        })
      )
    },
    async onError(error, variables, mutationResult, context) {
      await context.client.cancelQueries({
        queryKey: ChatReportQueryKeys.CHAT_SESSION_REPORTS(sessionId)
      })

      context.client.setQueryData(
        ChatReportQueryKeys.CHAT_SESSION_REPORTS(sessionId),
        /** @param {ChatReport[]} reports */
        reports => reports?.map(report => {
          if (report._id !== variables.reportId) return report
          return mutationResult.report
        })
      )
    }
  }),
}

export {
  ChatReportQueryKeys,
  ChatReportQueryOptions,
  ChatReportMutationOptions,
}
